import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Group, LoadingOverlay, Modal, Paper, Select, Stack, Table, Text, Textarea, TextInput, Title } from "@mantine/core";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";

// Contact Us inbox (GET /admin/contact-messages, PATCH /admin/contact-messages/:id).
// Every message from the landing page, website and app lands here. Mark each one In progress or Done so
// nothing is missed, and keep a short note on what was replied. Replies go out by email from the shared inbox.

const PAGE_SIZE = 50;
const STATUS = {
  NEW: { label: "New", color: "red" },
  IN_PROGRESS: { label: "In progress", color: "orange" },
  DONE: { label: "Done", color: "green" },
};
const TOPICS = [
  { value: "general", label: "General" },
  { value: "exhibitor", label: "Exhibitor" },
  { value: "sponsor", label: "Sponsor" },
  { value: "delete-account", label: "Delete account" },
  { value: "other", label: "Other" },
];
const SOURCES = [
  { value: "landing", label: "Landing page" },
  { value: "web", label: "Website" },
  { value: "app", label: "App" },
];
const EMPTY = { status: "NEW", topic: "", source: "", q: "" };

const getAdminHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
});

const formatTime = (iso) =>
  new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export default function ContactMessagesPage() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ NEW: 0, IN_PROGRESS: 0, DONE: 0 });
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [open, setOpen] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const { showError, showSuccess } = useNotification();

  const load = useCallback(
    async (before) => {
      setLoading(true);
      try {
        const query = new URLSearchParams({ limit: PAGE_SIZE });
        ["status", "topic", "source", "q"].forEach((k) => applied[k] && query.set(k, applied[k]));
        if (before) query.set("before", before);
        const res = await fetch(`${API_BASE_URL}/admin/contact-messages?${query}`, { headers: getAdminHeaders() });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          showError("Error", data.error || data.message || "Could not load messages.");
          return;
        }
        setRows((prev) => (before ? [...prev, ...data.data] : data.data));
        setHasMore(data.data.length === PAGE_SIZE);
        setCounts(data.counts);
      } catch {
        showError("Error", "Network error while loading messages.");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [applied],
  );

  useEffect(() => {
    load();
  }, [load]);

  const pickStatus = (status) => {
    const next = { ...filters, status };
    setFilters(next);
    setApplied(next);
  };
  const search = (e) => {
    e?.preventDefault();
    setApplied(filters);
  };
  const clear = () => {
    setFilters(EMPTY);
    setApplied(EMPTY);
  };

  const openRow = (row) => {
    setOpen(row);
    setNote(row.note || "");
  };

  const save = async (patch) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/contact-messages/${open.id}`, {
        method: "PATCH",
        headers: getAdminHeaders(),
        body: JSON.stringify(patch),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        showError("Error", data.error || data.message || "Could not save.");
        return;
      }
      showSuccess("Saved", "Message updated.");
      setOpen(null);
      load();
    } catch {
      showError("Error", "Network error while saving.");
    } finally {
      setSaving(false);
    }
  };

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.currentTarget.value }));

  return (
    <Stack gap="md" data-testid="contact-messages-page">
      <div>
        <Title order={3}>Contact Messages</Title>
        <Text c="dimmed" size="sm">
          Everything sent through the Contact Us form, newest first. Reply by email, then mark it Done.
        </Text>
      </div>

      <Group gap="xs">
        {Object.entries(STATUS).map(([value, s]) => (
          <Badge
            key={value}
            size="xl"
            color={s.color}
            variant={applied.status === value ? "filled" : "light"}
            style={{ cursor: "pointer" }}
            onClick={() => pickStatus(value)}
            data-testid={`contact-count-${value}`}
          >
            {s.label} {counts[value] ?? 0}
          </Badge>
        ))}
        <Badge size="xl" color="gray" variant={applied.status === "" ? "filled" : "light"} style={{ cursor: "pointer" }} onClick={() => pickStatus("")}>
          All
        </Badge>
      </Group>

      <Paper withBorder p="md" radius="md" component="form" onSubmit={search}>
        <Group align="flex-end" wrap="wrap">
          <Select label="Topic" placeholder="All" data={TOPICS} value={filters.topic || null} onChange={(v) => setFilters((f) => ({ ...f, topic: v || "" }))} clearable w={170} />
          <Select label="From" placeholder="All" data={SOURCES} value={filters.source || null} onChange={(v) => setFilters((f) => ({ ...f, source: v || "" }))} clearable w={170} />
          <TextInput label="Search" placeholder="Name, email, order or words" value={filters.q} onChange={setFilter("q")} w={280} />
          <Button type="submit">Search</Button>
          <Button variant="subtle" onClick={clear}>
            Clear
          </Button>
        </Group>
      </Paper>

      <Paper withBorder radius="md" pos="relative" style={{ overflowX: "auto" }}>
        <LoadingOverlay visible={loading} />
        <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
          <Table.Thead bg="gray.0">
            <Table.Tr>
              <Table.Th>Time</Table.Th>
              <Table.Th>From</Table.Th>
              <Table.Th>Topic</Table.Th>
              <Table.Th>Message</Table.Th>
              <Table.Th>Status</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.length === 0 && !loading && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" ta="center" py="lg">
                    No messages for this search.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {rows.map((row) => (
              <Table.Tr key={row.id} style={{ cursor: "pointer" }} onClick={() => openRow(row)} data-testid="contact-message-row">
                <Table.Td>
                  <Text size="sm">{formatTime(row.createdAt)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600}>
                    {row.name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {row.email}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{row.topic}</Text>
                  <Text size="xs" c="dimmed">
                    {row.source}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" lineClamp={2} maw={420}>
                    {row.message}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" color={STATUS[row.status]?.color || "gray"}>
                    {STATUS[row.status]?.label || row.status}
                  </Badge>
                  {row.handledByEmail && (
                    <Text size="xs" c="dimmed">
                      {row.handledByEmail}
                    </Text>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {hasMore && (
        <Group justify="center">
          <Button variant="default" loading={loading} onClick={() => load(rows[rows.length - 1]?.createdAt)}>
            Load more
          </Button>
        </Group>
      )}

      <Modal opened={!!open} onClose={() => setOpen(null)} title={open ? `${open.name} (${open.topic}, ${open.source})` : ""} size="lg">
        {open && (
          <Stack gap="sm" data-testid="contact-message-modal">
            <Group gap="md">
              <Text size="sm">
                <a href={`mailto:${open.email}`}>{open.email}</a>
              </Text>
              {open.phone && <Text size="sm">{open.phone}</Text>}
              {open.orderId && (
                <Text size="sm" ff="monospace">
                  Order {open.orderId}
                </Text>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              Received {formatTime(open.createdAt)}
              {open.handledAt ? `, handled ${formatTime(open.handledAt)} by ${open.handledByEmail || "-"}` : ""}
            </Text>
            <Paper withBorder p="sm" radius="md" bg="gray.0">
              <Text size="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {open.message}
              </Text>
            </Paper>
            <Textarea label="Note (what was replied, internal only)" value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={2} maxLength={2000} />
            <Group justify="space-between">
              <Button variant="default" loading={saving} onClick={() => save({ note })}>
                Save note
              </Button>
              <Group>
                {open.status !== "NEW" && (
                  <Button variant="subtle" color="red" loading={saving} onClick={() => save({ status: "NEW", note })}>
                    Reopen
                  </Button>
                )}
                {open.status !== "IN_PROGRESS" && (
                  <Button variant="light" color="orange" loading={saving} onClick={() => save({ status: "IN_PROGRESS", note })}>
                    In progress
                  </Button>
                )}
                {open.status !== "DONE" && (
                  <Button color="green" loading={saving} onClick={() => save({ status: "DONE", note })}>
                    Mark done
                  </Button>
                )}
              </Group>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
